import React, { useState, useRef, useEffect } from 'react';
import { Card } from '../card/Card.tsx';
import colors from '../../utils/colors';
import typography from '../../utils/typography';
import { EditIcon, ReplyIcon, ChatBubbleSparkleIcon, CloseIcon, CreateIcon } from '../../utils/icons';
import SmallButton from './SmallButton';
import MediumButton from './MediumButton';
import LargeButton from './LargeButton';
import LyricsComponent from '../forms/LyricsComponent';
import StyleDescription from '../forms/StyleDescription';

const IncomingLyrics = ({ lyrics, styles, onEdit, onStylesEdit, onTextSelect, selectedSongTitle, onOpenLyricsEditor, onOpenStylesEditor }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const [editedLyrics, setEditedLyrics] = useState(lyrics);
  const [editedStyles, setEditedStyles] = useState(styles);
  const [stylesSheetOpen, setStylesSheetOpen] = useState(false);
  const [lyricsSheetOpen, setLyricsSheetOpen] = useState(false);
  const [stylesCollapsed, setStylesCollapsed] = useState(true);
  const [lyricsCollapsed, setLyricsCollapsed] = useState(false);
  const [stylesEditorStylesCollapsed, setStylesEditorStylesCollapsed] = useState(false);
  const [stylesEditorLyricsCollapsed, setStylesEditorLyricsCollapsed] = useState(true);
  const containerRef = useRef(null);

  // Ensure only one component is expanded at a time in lyrics editor
  const handleStylesToggle = () => {
    if (stylesCollapsed) {
      // Expanding styles, collapse lyrics
      setStylesCollapsed(false);
      setLyricsCollapsed(true);
    } else {
      // Collapsing styles
      setStylesCollapsed(true);
    }
  };

  const handleLyricsToggle = () => {
    if (lyricsCollapsed) {
      // Expanding lyrics, collapse styles
      setLyricsCollapsed(false);
      setStylesCollapsed(true);
    } else {
      // Collapsing lyrics
      setLyricsCollapsed(true);
    }
  };

  // Ensure only one component is expanded at a time in styles editor
  const handleStylesEditorStylesToggle = () => {
    if (stylesEditorStylesCollapsed) {
      // Expanding styles, collapse lyrics
      setStylesEditorStylesCollapsed(false);
      setStylesEditorLyricsCollapsed(true);
    } else {
      // Collapsing styles
      setStylesEditorStylesCollapsed(true);
    }
  };

  const handleStylesEditorLyricsToggle = () => {
    if (stylesEditorLyricsCollapsed) {
      // Expanding lyrics, collapse styles
      setStylesEditorLyricsCollapsed(false);
      setStylesEditorStylesCollapsed(true);
    } else {
      // Collapsing lyrics
      setStylesEditorLyricsCollapsed(true);
    }
  };

  // Parse lyrics to extract sections
  const lyricsLines = lyrics.split('\n');
  const previewLines = lyricsLines.slice(0, 8); // Show first 8 lines as preview
  const displayLines = isExpanded ? lyricsLines : previewLines;
  const hasMore = lyricsLines.length > previewLines.length;

  // Handle text selection
  const handleMouseUp = () => {
    const selection = window.getSelection();
    const text = selection?.toString().trim();

    if (text && containerRef.current) {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();

      // Position tooltip above the selection
      setTooltipPosition({
        x: rect.left - containerRect.left + (rect.width / 2),
        y: rect.top - containerRect.top - 10
      });
      setSelectedText(text);
      setShowTooltip(true);
    } else {
      setShowTooltip(false);
    }
  };

  // Handle click outside to hide tooltip
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowTooltip(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSendToChat = () => {
    if (onTextSelect && selectedText) {
      onTextSelect(selectedText);
      setShowTooltip(false);
      window.getSelection()?.removeAllRanges();
    }
  };

  return (
    <div className="flex justify-start">
      <div
        ref={containerRef}
        className="flex flex-col gap-3 p-4 rounded-2xl max-w-[80%] relative"
        style={{
          backgroundColor: colors.bg.fog.thin,
          minWidth: '400px'
        }}
        onMouseUp={handleMouseUp}
      >
        {/* Styles Section */}
        {styles && (
          <div className="flex flex-col gap-3 w-full">
            {/* Styles Header */}
            <div className="flex items-center justify-between w-full">
              <p
                style={{
                  ...typography.medium.regular,
                  color: colors.text.primary
                }}
              >
                Styles
              </p>
              <Card.Root presented={stylesSheetOpen} onPresentedChange={setStylesSheetOpen}>
                <Card.Trigger asChild>
                  <div>
                    <SmallButton
                      variant="secondary"
                      leftIcon={<EditIcon />}
                    />
                  </div>
                </Card.Trigger>

                {/* Styles Editor Card */}
                <Card.Portal>
                  <Card.View>
                    <Card.Backdrop />
                    <Card.Content className="Card-content--large">
                      <div className="flex flex-col gap-3 p-6 h-full max-w-2xl">
                        {/* Header */}
                        <div className="flex items-center justify-between flex-shrink-0">
                          <p
                            style={{
                              ...typography.large.title,
                              color: colors.text.primary
                            }}
                          >
                            Edit Styles
                          </p>
                          <Card.Trigger action="dismiss" asChild>
                            <div>
                              <SmallButton
                                variant="secondary"
                                leftIcon={<CloseIcon />}
                              />
                            </div>
                          </Card.Trigger>
                        </div>

                        {/* Style Description Component - Expanded by default */}
                        <StyleDescription
                          description={editedStyles}
                          onDescriptionChange={setEditedStyles}
                          collapsed={stylesEditorStylesCollapsed}
                          onCollapsedChange={handleStylesEditorStylesToggle}
                        />

                        {/* Lyrics Component - Collapsed by default */}
                        <LyricsComponent
                          lyrics={editedLyrics}
                          onLyricsChange={setEditedLyrics}
                          collapsed={stylesEditorLyricsCollapsed}
                          onCollapsedChange={handleStylesEditorLyricsToggle}
                        />

                        {/* Create Button */}
                        <div className="flex-shrink-0 w-full">
                          <LargeButton
                            variant="aura"
                            text="Create new version"
                            leftIcon={<CreateIcon />}
                            className="w-full"
                            onClick={() => {
                              if (onOpenStylesEditor) {
                                onOpenStylesEditor(editedStyles);
                              }
                              setStylesSheetOpen(false);
                            }}
                          />
                        </div>
                      </div>
                    </Card.Content>
                  </Card.View>
                </Card.Portal>
              </Card.Root>
            </div>

            {/* Styles Content */}
            <div
              style={{
                ...typography.medium.regular,
                color: colors.text.secondary
              }}
            >
              {styles}
            </div>
          </div>
        )}

        {/* Divider */}
        {styles && (
          <div
            className="w-full"
            style={{
              height: '1px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }}
          />
        )}

        {/* Lyrics Section */}
        <div className="flex flex-col gap-3 w-full">
          {/* Lyrics Header */}
          <div className="flex items-center justify-between w-full">
            <p
              style={{
                ...typography.medium.regular,
                color: colors.text.primary
              }}
            >
              Lyrics
            </p>
            <Card.Root presented={lyricsSheetOpen} onPresentedChange={setLyricsSheetOpen}>
              <Card.Trigger asChild>
                <div>
                  <SmallButton
                    variant="secondary"
                    leftIcon={<EditIcon />}
                  />
                </div>
              </Card.Trigger>

              {/* Lyrics Editor Card */}
              <Card.Portal>
                <Card.View>
                  <Card.Backdrop />
                  <Card.Content className="Card-content--large">
                    <div className="flex flex-col gap-3 p-6 h-full max-w-2xl">
                      {/* Header */}
                      <div className="flex items-center justify-between flex-shrink-0">
                        <p
                          style={{
                            ...typography.large.title,
                            color: colors.text.primary
                          }}
                        >
                          Edit Lyrics
                        </p>
                        <Card.Trigger action="dismiss" asChild>
                          <div>
                            <SmallButton
                              variant="secondary"
                              leftIcon={<CloseIcon />}
                            />
                          </div>
                        </Card.Trigger>
                      </div>

                      {/* Style Description Component - Collapsed by default */}
                        <StyleDescription
                          description={editedStyles}
                          onDescriptionChange={setEditedStyles}
                          collapsed={stylesCollapsed}
                          onCollapsedChange={handleStylesToggle}
                        />
            

                      {/* Lyrics Component */}
  
                        <LyricsComponent
                          lyrics={editedLyrics}
                          onLyricsChange={setEditedLyrics}
                          collapsed={lyricsCollapsed}
                          onCollapsedChange={handleLyricsToggle}
                        />
  

                      {/* Create Button */}
                      <div className="flex-shrink-0 w-full">
                        <LargeButton
                          variant="aura"
                          text="Create new version"
                          leftIcon={<CreateIcon />}
                          className="w-full"
                          onClick={() => {
                            if (onOpenLyricsEditor) {
                              onOpenLyricsEditor(editedLyrics);
                            }
                            setLyricsSheetOpen(false);
                          }}
                        />
                      </div>
                    </div>
                  </Card.Content>
                </Card.View>
              </Card.Portal>
            </Card.Root>
          </div>

          {/* Lyrics Content */}
          <div className="flex flex-col gap-3">
            <div
              className="whitespace-pre-line"
              style={{
                ...typography.medium.regular,
                color: colors.text.secondary,
                lineHeight: '24px'
              }}
            >
              {displayLines.join('\n')}
            </div>

            {/* See More/Less Button */}
            {hasMore && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                style={{
                  ...typography.medium.title,
                  color: colors.accent.brand,
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                {isExpanded ? 'See less' : 'See more'}
              </button>
            )}
          </div>
        </div>

        {/* Text Selection Tooltip */}
        {showTooltip && (
          <button
            onClick={handleSendToChat}
            className="absolute backdrop-blur-[50px] flex items-center gap-2 px-3 py-2 rounded-full hover:opacity-90 transition-opacity cursor-pointer"
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
              transform: 'translate(-50%, -100%)',
              backgroundImage: "url('/aura.png')",
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              zIndex: 1000
            }}
          >
            <div
              className="absolute inset-0 rounded-full pointer-events-none"
              style={{
                border: `1px solid ${colors.border.primary}`,
                boxShadow: '0px 1px 16px 0px rgba(0, 0, 0, 0.3)'
              }}
            />
            <div className="w-4 h-4 flex-shrink-0 relative flex items-center justify-center" style={{ color: colors.text.primary }}>
              <ChatBubbleSparkleIcon />
            </div>
            <span
              className="relative"
              style={{
                ...typography.small.title,
                color: colors.text.primary
              }}
            >
              Send to chat
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default IncomingLyrics;